import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../../firebase/config';

const DashboardLayout = () => {
  const { currentUser, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState(null);
  const [userName, setUserName] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  useEffect(() => {
    const fetchUserProfileData = async () => {
      if (currentUser) {
        try {
          // First set basic info from currentUser
          setUserName(currentUser.displayName || currentUser.email?.split('@')[0] || 'User');
          
          // Try to get enhanced profile data from Firestore
          const userProfileDoc = await getDoc(doc(firestore, 'userProfiles', currentUser.uid));
          if (userProfileDoc.exists()) {
            const profileData = userProfileDoc.data();
            
            // Update user name with profile data if available
            if (profileData.firstName && profileData.lastName) {
              setUserName(`${profileData.firstName} ${profileData.lastName}`);
            } else if (profileData.firstName) {
              setUserName(profileData.firstName);
            }
            
            // Handle profile image from Firestore profile data
            if (profileData.profileImagePath || profileData.photoURL) {
              const imagePath = profileData.photoURL || profileData.profileImagePath;
              
              if (imagePath) {
                // If the path already starts with http/https, use it directly
                if (imagePath.startsWith('http')) {
                  setProfileImage(imagePath);
                } else {
                  try {
                    // Otherwise try to get image from Firebase Storage
                    const storagePath = imagePath.startsWith('userProfilePhoto/') 
                      ? imagePath 
                      : `userProfilePhoto/${currentUser.uid}`;
                    
                    const imageRef = ref(storage, storagePath);
                    const url = await getDownloadURL(imageRef);
                    setProfileImage(url);
                  } catch (imageError) {
                    console.log('Error fetching profile image from storage:', imageError);
                    // Fallback to currentUser photoURL if available
                    if (currentUser.photoURL) {
                      setProfileImage(currentUser.photoURL);
                    }
                  }
                }
              }
            } else if (currentUser.photoURL) {
              // Fallback to currentUser photoURL if no profile image in Firestore
              setProfileImage(currentUser.photoURL);
            }
          } else if (currentUser.photoURL) {
            // Fallback to currentUser photoURL if no Firestore profile
            setProfileImage(currentUser.photoURL);
          }
        } catch (error) {
          console.error('Error fetching user profile data:', error);
          // Fallback to basic currentUser data
          setUserName(currentUser.displayName || currentUser.email?.split('@')[0] || 'User');
          if (currentUser.photoURL) {
            setProfileImage(currentUser.photoURL);
          }
        }
      }
    };

    fetchUserProfileData();
  }, [currentUser]);
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/jobs?query=${encodeURIComponent(searchQuery)}`);
    }
  };
  
  const NavItem = ({ to, icon, label }) => {
    // This will match paths like /dashboard/home and /dashboard/home/subpath
    const isActive = location.pathname.startsWith(to);
    
    return (
      <Link
        to={to}
        className={`flex items-center px-4 py-3.5 text-sm font-medium rounded-lg transition-all duration-200 ${
          isActive 
            ? 'bg-[#FF8C00]/10 text-[#FF8C00] border-l-4 border-[#FF8C00]' 
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <span className="w-5 h-5">{icon}</span>
        <span className="ml-3">{label}</span>
      </Link>
    );
  };
  
  return (
    <div className="flex min-h-screen bg-gray-50 relative">
      {/* Top Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-10 px-4 md:px-6 h-16 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center">
          {/* Mobile menu button */}
          <button 
            className="md:hidden mr-2 text-gray-600 hover:text-[#FF8C00] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/asset/Logo/hireeLogo.png" alt="TableChecker" className="h-9 w-auto" />
            <span className="ml-2.5 text-xl font-bold hidden md:block text-gray-800">Hiree</span>
          </Link>
        </div>
        
        {/* Search Bar */}
        <div className="hidden md:block flex-1 max-w-xl mx-6">
          <form onSubmit={handleSearch} className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Find jobs"
              className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        </div>
        
        {/* User Menu */}
        <div className="flex items-center space-x-4">
          <button className="hidden md:flex items-center text-sm text-gray-700 hover:text-[#FF8C00]">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
            </svg>
            <span>Notifications</span>
          </button>
          
          <div className="relative">
            <button className="flex items-center space-x-2 focus:outline-none group">
              <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-2 border-transparent group-hover:border-[#FF8C00] transition-all">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-gray-600 font-medium">{userName.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <span className="hidden md:block text-sm font-medium text-gray-700 group-hover:text-[#FF8C00] transition-colors">{userName}</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="hidden md:block h-4 w-4 text-gray-500 group-hover:text-[#FF8C00]" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </header>
      
      {/* Sidebar Navigation */}
      <aside className={`fixed md:sticky top-0 inset-y-0 left-0 z-20 flex-shrink-0 w-72 bg-white border-r border-gray-200 overflow-y-auto transition-transform duration-300 ease-in-out md:translate-x-0 md:top-16 md:h-[calc(100vh-4rem)] ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } flex flex-col`}>
        {/* Mobile close button */}
        <div className="md:hidden p-4 flex justify-end">
          <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="text-gray-500 hover:text-gray-800"
            aria-label="Close menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Sidebar Header */}
        <div className="p-5 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Dashboard</h2>
        </div>
        
        {/* Navigation Links */}
        <nav className="p-4 space-y-1.5 flex-1">
          <NavItem 
            to="/dashboard/home" 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
              </svg>
            } 
            label="Home" 
          />
          
          <NavItem 
            to="/dashboard/jobs" 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
              </svg>
            } 
            label="Browse Jobs" 
          />
          
          <NavItem 
            to="/dashboard/businesses" 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1v1a1 1 0 11-2 0v-1H7v1a1 1 0 11-2 0v-1a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
              </svg>
            } 
            label="Browse Businesses" 
          />
          
          <NavItem 
            to="/dashboard/map" 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" />
              </svg>
            } 
            label="Job Map" 
          />
          
          <NavItem 
            to="/dashboard/chat" 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
              </svg>
            } 
            label="Chat" 
          />
          
          <NavItem 
            to="/dashboard/updates" 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
            } 
            label="Updates" 
          />
          
          <NavItem 
            to="/dashboard/profile" 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            } 
            label="Profile" 
          />
          
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full text-left rounded-lg transition-all duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-2 0V4H4v12h11v-2a1 1 0 012 0v3a1 1 0 01-1 1H4a1 1 0 01-1-1V3z" clipRule="evenodd" />
                <path fillRule="evenodd" d="M6 10a1 1 0 011-1h7.586l-2.293-2.293a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H7a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              <span className="ml-3">Logout</span>
            </button>
          </div>
        </nav>
        
        {/* App Download Section - At Bottom of Sidebar */}
        <div className="mt-auto p-4 border-t border-gray-200">
          <h3 className="text-md font-semibold text-gray-900 mb-1">Don't miss a great application</h3>
          <p className="text-sm text-gray-600 mb-3">
            Get the app to connect with candidates instantly and never miss an important message!
          </p>
          <div className="flex space-x-2 items-center justify-center">
            <a 
              href="https://apps.apple.com/us/app/hiree/id6740465535" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-1/2"
            >
              <img 
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                alt="Download on the App Store" 
                className="h-10 w-full"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg';
                }}
              />
            </a>
            
            {/* <div className="block relative w-1/2">
              <img 
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                alt="Get it on Google Play" 
                className="h-10 w-full opacity-70"
              />
              <div className="absolute text-[8px] text-black font-medium bottom-1 right-2 italic">Coming Soon</div>
            </div> */}
          </div>
        </div>
      </aside>
      
      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 md:hidden z-10"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}
      
      {/* Main Content */}
      <main className="flex-1 pt-16 pb-6 overflow-auto">
        <div className="h-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout; 